import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiHref } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePlant } from "@/context/PlantContext";
import { localToday } from "@/lib/dates";
import { Input } from "@/components/ui/input";
import {
  Printer, FileDown, FileText, ClipboardList, Droplets, CloudRain,
  TrendingUp, CalendarDays, Rocket, Megaphone, ExternalLink, FlaskConical, PlugZap, Building2, ShieldCheck,
} from "lucide-react";

const addDays = (iso, n) => { const d = new Date(`${iso}T00:00:00`); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const thisMonth = () => localToday().slice(0, 7);

function OpenBtn({ href, testid, label = "Open / Print" }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" data-testid={testid}
      className="inline-flex items-center gap-2 h-9 px-3 bg-primary-orange text-white text-xs font-bold uppercase tracking-wide hover:opacity-90">
      <Printer className="w-3.5 h-3.5" /> {label}
    </a>
  );
}

function Card({ icon: Icon, title, desc, children, testid }) {
  return (
    <div className="card-industrial p-5 flex flex-col gap-3" data-testid={testid}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 shrink-0 border border-industrial bg-surface flex items-center justify-center">
          <Icon className="w-5 h-5 text-primary-orange" />
        </div>
        <div>
          <div className="heading text-base font-black uppercase">{title}</div>
          <p className="text-xs text-muted-industrial mt-0.5">{desc}</p>
        </div>
      </div>
      <div className="mt-auto flex flex-wrap items-end gap-2">{children}</div>
    </div>
  );
}

export default function PrintCentre() {
  const { user } = useAuth();
  const { plantId } = usePlant();
  const plantQ = plantId ? `&plant_id=${plantId}` : "";
  const isSuper = !!user?.is_super_admin;

  const [orgs, setOrgs] = useState([]);
  const [orgId, setOrgId] = useState("");
  const [saInvoices, setSaInvoices] = useState([]);
  const [saInvoice, setSaInvoice] = useState("");

  const [oosDate, setOosDate] = useState(localToday());
  const [dayDate, setDayDate] = useState(localToday());
  const [from, setFrom] = useState(addDays(localToday(), -13));
  const [to, setTo] = useState(localToday());
  const [month, setMonth] = useState(thisMonth());

  const [dockets, setDockets] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [docket, setDocket] = useState("");
  const [invoice, setInvoice] = useState("");

  useEffect(() => {
    api.get("/dockets?limit=60").then((r) => {
      const rows = Array.isArray(r.data) ? r.data : (r.data.dockets || r.data.items || []);
      setDockets(rows);
      if (rows[0]) setDocket(rows[0].id);
    }).catch(() => {});
    api.get("/invoices").then((r) => {
      const rows = Array.isArray(r.data) ? r.data : (r.data.invoices || r.data.items || []);
      setInvoices(rows);
      if (rows[0]) setInvoice(rows[0].id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isSuper) return;
    api.get("/superadmin/clients").then((r) => {
      const rows = r.data?.clients || r.data || [];
      setOrgs(rows);
      if (rows[0]) setOrgId(rows[0].id);
    }).catch(() => {});
  }, [isSuper]);

  useEffect(() => {
    if (!isSuper || !orgId) return;
    setSaInvoice("");
    api.get(`/superadmin/orgs/${orgId}/invoices`).then((r) => {
      const rows = Array.isArray(r.data) ? r.data : (r.data.invoices || []);
      setSaInvoices(rows);
      if (rows[0]) setSaInvoice(rows[0].id);
    }).catch(() => setSaInvoices([]));
  }, [isSuper, orgId]);

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="print-centre-page">
      <div>
        <div className="label-tag">DOCUMENTS · PRINT CENTRE</div>
        <h1 className="heading text-3xl font-black uppercase flex items-center gap-2">
          <Printer className="w-7 h-7 text-primary-orange" /> Print Centre
        </h1>
        <p className="text-sm text-muted-industrial mt-1">
          Every printable PDF in one place. Pick a date or record and open a branded, print-ready document.
        </p>
      </div>

      <div className="label-tag">Reports &amp; standalone pages</div>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        <Card icon={Droplets} title="Out-of-Spec Water (EOD)" testid="pc-oos"
          desc="End-of-day loads that added water over AS 1379 or ran over their locked W/C spec.">
          <Input type="date" data-testid="pc-oos-date" value={oosDate} onChange={(e) => setOosDate(e.target.value)}
            className="h-9 w-40 rounded-none bg-transparent border-industrial" />
          <OpenBtn testid="pc-oos-open" href={apiHref(`/analytics/out-of-spec.pdf?date=${oosDate}${plantQ}`)} />
        </Card>

        <Card icon={FlaskConical} title="AI Slump Log — Quality Report" testid="pc-slump"
          desc="Plant vs site water and W/C drift per pour across a date range.">
          <Input type="date" data-testid="pc-slump-from" value={from} onChange={(e) => setFrom(e.target.value)}
            className="h-9 w-36 rounded-none bg-transparent border-industrial" />
          <span className="mono text-xs text-muted-industrial self-center">→</span>
          <Input type="date" data-testid="pc-slump-to" value={to} onChange={(e) => setTo(e.target.value)}
            className="h-9 w-36 rounded-none bg-transparent border-industrial" />
          <OpenBtn testid="pc-slump-open" href={apiHref(`/analytics/slump-log.pdf?date_from=${from}&date_to=${to}${plantQ}`)} />
        </Card>

        <Card icon={CloudRain} title="Pour Conditions Warnings" testid="pc-pour"
          desc="Adverse-weather notices sent to customers and their replies — the job-file evidence trail.">
          <Input type="date" data-testid="pc-pour-from" value={from} onChange={(e) => setFrom(e.target.value)}
            className="h-9 w-36 rounded-none bg-transparent border-industrial" />
          <span className="mono text-xs text-muted-industrial self-center">→</span>
          <Input type="date" data-testid="pc-pour-to" value={to} onChange={(e) => setTo(e.target.value)}
            className="h-9 w-36 rounded-none bg-transparent border-industrial" />
          <OpenBtn testid="pc-pour-open" href={apiHref(`/analytics/pour-warnings.pdf?date_from=${from}&date_to=${to}${plantQ}`)} />
        </Card>

        <Card icon={TrendingUp} title="Daily Summary" testid="pc-daily"
          desc="One-page end-of-day operations snapshot: volumes, loads, customers.">
          <Input type="date" data-testid="pc-daily-date" value={dayDate} onChange={(e) => setDayDate(e.target.value)}
            className="h-9 w-40 rounded-none bg-transparent border-industrial" />
          <OpenBtn testid="pc-daily-open" href={apiHref(`/admin/daily-summary.pdf?date=${dayDate}`)} />
        </Card>

        <Card icon={CalendarDays} title="Monthly Delivery Calendar" testid="pc-month"
          desc="Delivered volume and loads laid out day-by-day for the whole month.">
          <Input type="month" data-testid="pc-month-val" value={month} onChange={(e) => setMonth(e.target.value)}
            className="h-9 w-44 rounded-none bg-transparent border-industrial" />
          <OpenBtn testid="pc-month-open" href={apiHref(`/reports/monthly-calendar.pdf?month=${month}`)} />
        </Card>

        <Card icon={Rocket} title="Getting Started Guide" testid="pc-welcome"
          desc="Branded onboarding one-pager for new staff and crews.">
          <OpenBtn testid="pc-welcome-open" href={apiHref("/onboarding/welcome.pdf")} />
        </Card>
      </div>

      <div className="label-tag pt-2">Per-record documents</div>
      <div className="grid md:grid-cols-2 gap-4">
        <Card icon={ClipboardList} title="Delivery Docket" testid="pc-docket"
          desc="Choose a recent docket to print its delivery docket and weigh-batch record.">
          <select data-testid="pc-docket-select" value={docket} onChange={(e) => setDocket(e.target.value)}
            className="h-9 min-w-[12rem] max-w-full rounded-none bg-transparent border border-industrial px-2 text-sm">
            {dockets.length === 0 && <option value="">No dockets</option>}
            {dockets.map((d) => (
              <option key={d.id} value={d.id}>
                {d.docket_no} · {d.customer_name || ""}{d.mix_code ? ` · ${d.mix_code}` : ""}
              </option>
            ))}
          </select>
          {docket && <OpenBtn testid="pc-docket-open" label="Docket PDF" href={apiHref(`/dockets/${docket}/pdf`)} />}
          {docket && (
            <a href={apiHref(`/dockets/${docket}/batch-record.pdf`)} target="_blank" rel="noreferrer" data-testid="pc-docket-batch"
              className="inline-flex items-center gap-2 h-9 px-3 border border-industrial text-xs font-bold uppercase tracking-wide hover:bg-surface">
              <FileDown className="w-3.5 h-3.5" /> Batch record
            </a>
          )}
        </Card>

        <Card icon={FileText} title="Tax Invoice" testid="pc-invoice"
          desc="Choose an invoice to open its branded PDF for printing or re-sending.">
          <select data-testid="pc-invoice-select" value={invoice} onChange={(e) => setInvoice(e.target.value)}
            className="h-9 min-w-[12rem] max-w-full rounded-none bg-transparent border border-industrial px-2 text-sm">
            {invoices.length === 0 && <option value="">No invoices</option>}
            {invoices.map((i) => (
              <option key={i.id} value={i.id}>
                {i.invoice_no} · {i.customer_name || ""}{i.total != null ? ` · $${Number(i.total).toLocaleString()}` : ""}
              </option>
            ))}
          </select>
          {invoice && <OpenBtn testid="pc-invoice-open" label="Invoice PDF" href={apiHref(`/invoices/${invoice}/pdf`)} />}
        </Card>
      </div>

      {isSuper && (
        <>
          <div className="label-tag pt-2 flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5 text-primary-orange" /> Super-Admin documents</div>
          <div className="grid md:grid-cols-2 gap-4">
            <Card icon={Building2} title="SaaS Subscription Invoice" testid="pc-sa-invoice"
              desc="Pick an organisation and one of its subscription invoices to open & print.">
              <select data-testid="pc-sa-org-select" value={orgId} onChange={(e) => setOrgId(e.target.value)}
                className="h-9 min-w-[11rem] max-w-full rounded-none bg-transparent border border-industrial px-2 text-sm">
                {orgs.length === 0 && <option value="">No organisations</option>}
                {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
              <select data-testid="pc-sa-invoice-select" value={saInvoice} onChange={(e) => setSaInvoice(e.target.value)}
                className="h-9 min-w-[11rem] max-w-full rounded-none bg-transparent border border-industrial px-2 text-sm">
                {saInvoices.length === 0 && <option value="">No invoices for this org</option>}
                {saInvoices.map((i) => <option key={i.id} value={i.id}>{i.invoice_number || i.id.slice(0, 8)}</option>)}
              </select>
              {saInvoice && <OpenBtn testid="pc-sa-invoice-open" label="Invoice PDF" href={apiHref(`/superadmin/invoices/${saInvoice}/pdf`)} />}
            </Card>

            <Card icon={FileText} title="Client Software Agreement" testid="pc-sa-agreement"
              desc="Open & print the software licence agreement for the selected organisation.">
              <select data-testid="pc-sa-agreement-org" value={orgId} onChange={(e) => setOrgId(e.target.value)}
                className="h-9 min-w-[11rem] max-w-full rounded-none bg-transparent border border-industrial px-2 text-sm">
                {orgs.length === 0 && <option value="">No organisations</option>}
                {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
              {orgId && <OpenBtn testid="pc-sa-agreement-open" label="Agreement PDF" href={apiHref(`/superadmin/orgs/${orgId}/agreement/pdf`)} />}
            </Card>

            <Card icon={PlugZap} title="Batch System Connection Guide" testid="pc-connectors"
              desc="How to log into & connect Rinstrum, DornerBatch, Sysdyne, Command Alkon and Jonel.">
              <OpenBtn testid="pc-connectors-open" href={apiHref("/connectors/guide.pdf")} />
            </Card>

            <Card icon={Megaphone} title="Sales Flyer (A4)" testid="pc-flyer"
              desc="Standalone A4 marketing flyer — opens as a page, print straight from your browser.">
              <Link to="/flyer" target="_blank" data-testid="pc-flyer-open"
                className="inline-flex items-center gap-2 h-9 px-3 border border-industrial text-xs font-bold uppercase tracking-wide hover:bg-surface">
                <ExternalLink className="w-3.5 h-3.5" /> Open flyer page
              </Link>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
